import React, { useEffect, useState } from 'react';
import AWSAccountsHomeTableRow from './AWSAccountsHomeTableRow';
import AWSAccountsService from '../../../services/AWSAccountsService';
import SectionHomeWrapper from '../../common/wrappers/SectionHomeWrapper';
import { AlertBanner } from '../../../models/interfaces/AlertBanner';
import { getOrganizations } from '../../common/OrganizationStorage';
import Organization from '../../../models/entities/Organization';
import AWSAccountRequest from '../../../models/entities/AWSAccountRequest';
import { useNavigate } from 'react-router-dom';
import Paginator from '../../common/tables/Paginator';
import { Alert, AlertVariantType, ProgressBar, TextLink, TextPassage } from '../../common';
import AWSAccountsHomeFilters from './AWSAccountsHomeFilters';
import { Table, TableBody, TableFooter, TableHeader, TableHeaderItem } from '@emorg-prd/standard-react';

const memoized = {
	myAccounts: void 0,
	allAccounts: void 0,
};

const AWSAccountsHome: React.FC = () => {
	const [myAccounts, setMyAccounts] = useState<AWSAccountRequest[]>([]);
	const [allAWSAccounts, setAWSAllAccounts] = useState<AWSAccountRequest[]>([]);
	const [accountsOnPage, setAccountsOnPage] = useState<AWSAccountRequest[]>([]);
	const [filteredAWSAccounts, setFilteredAWSAccounts] = useState<AWSAccountRequest[]>([]);
	const [areMyAccountsLoading, setAreMyAccountsLoading] = useState<boolean>(true);
	const [areAllAccountsLoading, setAreAllAccountsLoading] = useState<boolean>(true);
	const [progressLabel, setProgressLabel] = useState<string>('Loading your accounts...');
	const [endModal, setEndModal] = useState<AlertBanner>({ visible: false });
	const [parentOrganizationsList, setParentOrganizationsList] = useState<Organization[]>([]);
	const [alert, setAlert] = useState<AlertBanner>({
		visible: false,
		variant: 'error',
		text: '',
	});
	const navigate = useNavigate();
	const awsAccountService = new AWSAccountsService();
	const accountRow: React.RefObject<HTMLBaseElement> = React.createRef();

	const fetchAccounts = async (isRefresh: boolean) => {
		setAreMyAccountsLoading(true);
		setAreAllAccountsLoading(true);
		setProgressLabel(`${isRefresh ? 'Reloading' : 'Loading'} your accounts...`);
		try {
			if (isRefresh) refreshCache();

			const myAccounts = await getAccounts('getMyAccounts', 'myAccounts');
			const organizations = (await getOrganizations()).map((org) => new Organization(org));
			setParentOrganizationsList(
				organizations.filter(
					(organization) => organization.parentOrganizationId == null && organization.status == 'Active'
				)
			);
			setMyAccounts(myAccounts);
			setAWSAllAccounts(mapAccounts(myAccounts, organizations));
			setAreMyAccountsLoading(false);
			setProgressLabel(`${isRefresh ? 'Reloading' : 'Loading'} all accounts...`);

			const allAccounts = await await getAccounts('getAccounts', 'allAccounts');
			setAWSAllAccounts(mapAccounts(allAccounts, organizations));
			setAreAllAccountsLoading(false);
		} catch (error) {
			console.log(error);
			handleEndModal('The accounts could not be retrieved.', 'error', 10000);
		}
	};

	const mapAccounts = (accounts: AWSAccountRequest[], organizations: Organization[]) => {
		//TODO: implement sorting logic with created Date
		// accounts.sort((a, b) => {
		//     const aa = a.azureState ? a.azureState : nullState;
		//     const bb = b.azureState ? b.azureState : nullState;
		//     if (aa === bb) {
		//         return (
		//             moment(b.workflowInstance.createdDate).valueOf() - moment(a.workflowInstance.createdDate).valueOf()
		//         );
		//     }
		//     if (aa === 'Enabled') return -1;
		//     if (bb === 'Enabled') return 1;
		//     if (aa > bb) return 1;
		//     if (aa < bb) return -1;
		//     return 0;
		// });
		return accounts.map((result) => {
			const account = new AWSAccountRequest(result);
			account.organization?.setLevels(organizations);
			return account;
		});
	};

	async function getAccounts(method: string, key: string) {
		if (typeof memoized[key] !== 'undefined') return memoized[key];
		let data = [];
		data = await awsAccountService[method]();
		memoized[key] = data;
		return data;
	}

	function refreshCache() {
		memoized.myAccounts = void 0;
		memoized.allAccounts = void 0;
	}

	const handleEndModal = (text: string, variant: AlertVariantType, timeout: number) => {
		setEndModal({
			visible: true,
			text,
			variant,
		});
		setTimeout(() => endAndRedirect(), timeout);
	};

	const endAndRedirect = () => {
		setEndModal({ visible: false });
		navigate('/');
	};

	const handleRefresh = () => {
		fetchAccounts(true);
	};

	const getFilterProps = () => {
		return {
			setFilteredAWSAccounts,
			areMyAccountsLoading,
			areAllAccountsLoading,
			setAreMyAccountsLoading,
			setAreAllAccountsLoading,
			filteredAWSAccounts,
			fetchAccounts,
			allAWSAccounts,
			accountsOnPage,
			parentOrganizationsList,
		};
	};

	useEffect(() => {
		fetchAccounts(false);
	}, []);

	return (
		<SectionHomeWrapper title="AWS Accounts">
			<TextPassage>
				<p>
					For more information, please follow{' '}
					<TextLink
						target="_blank"
						external
						href={'https://appwiki.xom.cloud/docs/SpecializedCloud/AWS/account-owner.html'}
					>
						this link
					</TextLink>
				</p>
			</TextPassage>
			{alert.visible && (
				<Alert variant={alert.variant} onClose={() => setAlert({ visible: false })} body={alert.text}></Alert>
			)}
			{(areMyAccountsLoading || areAllAccountsLoading) && (
				<ProgressBar className="em-u-margin-top-half" indeterminate label={progressLabel} />
			)}
			{!areMyAccountsLoading && (
				<>
					<AWSAccountsHomeFilters {...getFilterProps()} />
					<Table hasFooter hasPagination>
						<TableHeader>
							<TableHeaderItem> Account Name </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Type </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Status </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Organization </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Owner </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Custodian </TableHeaderItem>
							<TableHeaderItem optionalClass="table__header-cell"> Requested Date </TableHeaderItem>
							<TableHeaderItem> </TableHeaderItem>
						</TableHeader>
						<TableBody>
							{filteredAWSAccounts.map((account) => (
								<AWSAccountsHomeTableRow
									key={account.id}
									accountRowRef={accountRow}
									account={account}
								/>
							))}
						</TableBody>
					</Table>
				</>
			)}
		</SectionHomeWrapper>
	);
};

export default AWSAccountsHome;
